import { difference, union } from "lodash";
import {
  type Identifier,
  RecordContextProvider,
  RecordRepresentation,
  useListContext,
  useLocaleState,
  useTimeout,
  useTranslate,
} from "ra-core";
import { type MouseEvent, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router";
import { ReferenceField } from "@/components/admin/reference-field";
import { TextField } from "@/components/admin/text-field";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

import { StatusBadge } from "../../gravun/data-display/StatusBadge";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { Status } from "../misc/Status";
import { formatRelativeDate } from "../misc/RelativeDate";
import type { Contact } from "../types";
import { Avatar } from "./Avatar";
import { TagsList } from "./TagsList";
import { CopyCheck } from "lucide-react";
import {
  duplicateIdSet,
  useDuplicates,
} from "../../gravun/duplicates/useDuplicates";

export const ContactListContent = () => {
  const translate = useTranslate();
  const {
    data: contacts,
    error,
    isPending,
    onToggleItem,
    onSelect,
    selectedIds,
  } = useListContext<Contact>();
  const lastSelected = useRef<Identifier | null>(null);

  // Handle shift+click to select a range of rows
  const handleToggleItem = useCallback(
    (id: Identifier, event: MouseEvent) => {
      if (!contacts) return;

      const ids = contacts.map((contact) => contact.id);
      const lastSelectedIndex = lastSelected.current
        ? ids.indexOf(lastSelected.current)
        : -1;

      if (event.shiftKey && lastSelectedIndex !== -1) {
        const index = ids.indexOf(id);
        const idsBetweenSelections = ids.slice(
          Math.min(lastSelectedIndex, index),
          Math.max(lastSelectedIndex, index) + 1,
        );

        const isClickedItemSelected = selectedIds?.includes(id);
        const newSelectedIds = isClickedItemSelected
          ? difference(selectedIds, idsBetweenSelections)
          : union(selectedIds, idsBetweenSelections);

        onSelect?.(newSelectedIds);
      } else {
        onToggleItem(id);
      }

      lastSelected.current = id;
    },
    [contacts, selectedIds, onSelect, onToggleItem],
  );

  if (isPending) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {[...Array(6)].map((_, index) => (
          <Skeleton key={index} className="h-8 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return null;
  }

  const allSelected =
    contacts.length > 0 &&
    contacts.every((contact) => selectedIds.includes(contact.id));

  return (
    <table className="w-full">
      <thead>
        <tr className="border-b border-border text-left text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          <th className="w-10 px-3 py-2">
            <Checkbox
              aria-label={translate("ra.action.select_all")}
              checked={allSelected}
              onCheckedChange={() =>
                onSelect?.(allSelected ? [] : contacts.map((c) => c.id))
              }
            />
          </th>
          <th className="px-3 py-2 font-medium">
            {translate("resources.contacts.fields.name", { _: "Name" })}
          </th>
          <th className="px-3 py-2 font-medium">Company</th>
          <th className="px-3 py-2 font-medium">Email</th>
          <th className="px-3 py-2 font-medium">Phone</th>
          <th className="px-3 py-2 font-medium">Status</th>
          <th className="px-3 py-2 font-medium">Owner</th>
          <th className="px-3 py-2 text-right font-medium">Last activity</th>
        </tr>
      </thead>
      <tbody>
        {contacts.map((contact) => (
          <RecordContextProvider key={contact.id} value={contact}>
            <ContactItemContent
              contact={contact}
              handleToggleItem={handleToggleItem}
            />
          </RecordContextProvider>
        ))}
        {contacts.length === 0 && (
          <tr>
            <td
              colSpan={8}
              className="p-6 text-center text-[13px] text-muted-foreground"
            >
              {translate("resources.contacts.empty.title", {})}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

const ContactItemContent = ({
  contact,
  handleToggleItem,
}: {
  contact: Contact;
  handleToggleItem: (id: Identifier, event: MouseEvent) => void;
}) => {
  const [locale = "en"] = useLocaleState();
  const { selectedIds } = useListContext<Contact>();
  const { noteStatuses } = useConfigurationContext();
  const navigate = useNavigate();

  const primaryEmail = contact.email_jsonb?.[0]?.email;
  const primaryPhone = contact.phone_jsonb?.[0]?.number;
  const statusLabel = noteStatuses.find(
    (status) => status.value === contact.status,
  )?.label;
  // One workspace-wide detection query, shared and cached across every row.
  const { data: duplicatePairs } = useDuplicates();
  const isDuplicate = duplicateIdSet(duplicatePairs).has(Number(contact.id));

  return (
    <tr
      className="cursor-pointer border-b border-border-subtle transition-colors last:border-0 hover:bg-surface-muted"
      onClick={() => navigate(`/contacts/${contact.id}/show`)}
    >
      <td
        className="w-10 px-3 py-2"
        onClick={(event) => {
          event.stopPropagation();
          handleToggleItem(contact.id, event);
        }}
      >
        <Checkbox
          className="cursor-pointer"
          checked={selectedIds.includes(contact.id)}
          aria-label={`${contact.first_name} ${contact.last_name ?? ""}`}
        />
      </td>
      <td className="px-3 py-2">
        <Link
          to={`/contacts/${contact.id}/show`}
          className="flex items-center gap-2.5 no-underline"
          onClick={(event) => event.stopPropagation()}
        >
          <Avatar width={25} height={25} />
          <span className="min-w-0 leading-tight">
            <span className="flex min-w-0 items-center gap-1.5">
              <span className="truncate text-[13px] font-medium text-foreground">
                {contact.first_name} {contact.last_name ?? ""}
              </span>
              {/* Flagged in the list too, so a duplicate is visible before
                  anyone opens the record and starts working in the wrong one. */}
              {isDuplicate ? (
                <CopyCheck
                  className="size-3.5 shrink-0 text-amber-600"
                  strokeWidth={2}
                  aria-label="Possible duplicate"
                />
              ) : null}
            </span>
            {contact.title || contact.tags?.length ? (
              <span className="flex items-center gap-1.5 truncate text-[11.5px] text-muted-foreground">
                {contact.title}
                <TagsList />
              </span>
            ) : null}
          </span>
        </Link>
      </td>
      <td className="max-w-40 truncate px-3 py-2 text-[13px] text-foreground">
        {contact.company_id != null ? (
          <ReferenceField
            source="company_id"
            reference="companies"
            link={false}
          >
            <TextField source="name" />
          </ReferenceField>
        ) : (
          <span className="text-muted-foreground"> · </span>
        )}
      </td>
      <td className="max-w-48 truncate px-3 py-2 text-[13px] text-muted-foreground">
        {primaryEmail ?? "n/a"}
      </td>
      <td className="whitespace-nowrap px-3 py-2 text-[13px] text-muted-foreground">
        {primaryPhone ?? "n/a"}
      </td>
      <td className="px-3 py-2">
        {contact.status && statusLabel ? (
          <StatusBadge label={statusLabel} status={contact.status} />
        ) : (
          <span className="text-[13px] text-muted-foreground"> · </span>
        )}
      </td>
      <td className="max-w-32 truncate px-3 py-2 text-[13px] text-muted-foreground">
        {contact.sales_id != null ? (
          <ReferenceField source="sales_id" reference="sales" link={false}>
            <TextField source="first_name" />
          </ReferenceField>
        ) : (
          "n/a"
        )}
      </td>
      <td
        className="whitespace-nowrap px-3 py-2 text-right text-[12.5px] text-muted-foreground"
        title={contact.last_seen ?? undefined}
      >
        {contact.last_seen
          ? formatRelativeDate(contact.last_seen, locale)
          : "n/a"}
      </td>
    </tr>
  );
};

export const ContactListContentMobile = () => {
  const translate = useTranslate();
  const {
    data: contacts,
    error,
    isPending,
    refetch,
  } = useListContext<Contact>();
  const oneSecondHasPassed = useTimeout(1000);

  if (isPending) {
    if (!oneSecondHasPassed) {
      return null;
    }
    return (
      <>
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="flex flex-row items-center py-2 hover:bg-muted transition-colors first:rounded-t-xl last:rounded-b-xl"
          >
            <div className="flex flex-row gap-4 items-center mr-4">
              <Skeleton className="w-10 h-10 rounded-full" />
            </div>
            <div className="flex-1 min-w-0">
              <Skeleton className="w-32 h-5 mb-2" />
              <Skeleton className="w-48 h-4" />
            </div>
          </div>
        ))}
      </>
    );
  }

  if (error && !contacts) {
    return (
      <div className="p-4">
        <div className="text-center text-muted-foreground mb-4">
          {translate("resources.contacts.list.error_loading")}
        </div>
        <div className="text-center mt-2">
          <Button
            onClick={() => {
              refetch();
            }}
          >
            <RotateCcw />
            {translate("crm.common.retry")}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="md:divide-y">
      {contacts.map((contact) => (
        <RecordContextProvider key={contact.id} value={contact}>
          <ContactItemContentMobile contact={contact} />
        </RecordContextProvider>
      ))}
      {contacts.length === 0 && (
        <div className="p-4">
          <div className="text-muted-foreground">
            {translate("resources.contacts.empty.title")}
          </div>
        </div>
      )}
    </div>
  );
};

const ContactItemContentMobile = ({ contact }: { contact: Contact }) => {
  const { data: duplicatePairs } = useDuplicates();
  const isDuplicate = duplicateIdSet(duplicatePairs).has(Number(contact.id));
  const translate = useTranslate();
  return (
    <Link
      to={`/contacts/${contact.id}/show`}
      className="flex flex-row gap-4 items-center py-2 hover:bg-muted transition-colors"
    >
      <Avatar />
      <div className="flex flex-col grow justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex justify-between">
            <div className="flex min-w-0 items-center gap-1.5 font-medium">
              <RecordRepresentation />
              {isDuplicate ? (
                <CopyCheck
                  className="size-3.5 shrink-0 text-amber-600"
                  strokeWidth={2}
                  aria-label="Possible duplicate"
                />
              ) : null}
            </div>
            <Status status={contact.status} />
          </div>
          <div className="text-sm text-muted-foreground">
            <div className="flex flex-col gap-1">
              <span>
                {contact.title && contact.company_id != null
                  ? `${translate("resources.contacts.position_at", {
                      title: contact.title,
                    })} `
                  : contact.title}
                {contact.company_id != null && (
                  <ReferenceField
                    source="company_id"
                    reference="companies"
                    link={false}
                  >
                    <TextField source="name" />
                  </ReferenceField>
                )}
              </span>
              {contact.nb_tasks ? (
                <span>
                  {translate("crm.common.task_count", {
                    smart_count: contact.nb_tasks,
                  })}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};
